import { T, Var, useGT } from 'gt-react';
import { EnvelopeIcon, XIcon, EyeIcon, RobotIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { EmailDraftSummary } from '@erp/rpc';

interface EmailDraftCardProps {
  draft: EmailDraftSummary;
  onReviewDraft: () => void;
  onDismiss: () => void;
  className?: string;
}

export function EmailDraftCard({
  draft,
  onReviewDraft,
  onDismiss,
  className,
}: EmailDraftCardProps) {
  const gt = useGT();
  return (
    <div
      className={cn(
        'relative flex items-start gap-3 rounded-lg border p-3',
        'bg-blue-50 dark:bg-blue-950',
        'border-blue-200 dark:border-blue-800',
        className,
      )}
    >
      <Button
        size="sm"
        variant="ghost"
        onClick={onDismiss}
        className="absolute right-1 top-1 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
        aria-label={gt('Dismiss', {
          context: 'accessibility, dismiss email draft card',
        })}
      >
        <XIcon className="h-3.5 w-3.5" />
      </Button>

      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50">
        <EnvelopeIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
      </div>

      <div className="min-w-0 flex-1 pr-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            <T context="label for AI-generated email draft card">Email Draft</T>
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-900/50 dark:text-violet-300">
            <RobotIcon className="h-2.5 w-2.5" />
            AI
          </span>
        </div>
        <p className="mt-0.5 text-sm text-foreground">
          <T context="email draft card, recipient line">
            Draft reply to{' '}
            <span className="font-medium">
              <Var>{draft.to}</Var>
            </span>
          </T>
        </p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {draft.subject}
        </p>

        <div className="mt-2 flex gap-1.5">
          <Button
            size="sm"
            variant="default"
            onClick={onReviewDraft}
            className="h-7 bg-blue-600 px-2 text-xs hover:bg-blue-700"
          >
            <EyeIcon className="mr-1 h-3 w-3" />
            <T context="button, open email draft for review">Review Draft</T>
          </Button>
        </div>
      </div>
    </div>
  );
}
